import React from "react";
import { CollapsibleList, DateTimeUtils, Person, PriorityIcon, Table, TemperatureIcon, USER_PERMISSIONS } from "dyl-components";

import {
    KEY_CREATED, KEY_LAST_MODIFIED,
    KEY_ACCOUNT, KEY_STAGE, KEY_CAMPAIGN_SOURCE,
    KEY_LAST_MODIFIED_BY, KEY_CREATED_BY,
    KEY_OUTCOME, KEY_LOST_REASON, KEY_LEAD_TYPE,
    KEY_PRODUCT_INTEREST, KEY_LEAD_PRIORITY,
    KEY_LEAD_RATING, KEY_LEAD_SCORE, KEY_MASTER_SECONDARY,
    KEY_CLOSE_PROBABILITY, KEY_TOTAL_COST, KEY_ACQUISITION_COST,
    KEY_ADDITIONAL_COST, KEY_EXPECTED_REVENUE
} from 'shared/constants/MODULE_COLUMNS';

import { Header } from "semantic-ui-react";
import { Link, useParams } from "react-router-dom";
import Contact from "shared/Contact";
import ModuleLink from "shared/ModuleLink";
import RestrictedView from "shared/RestrictedView";

const LeadsRow = ({ lead, isColumnHidden, priority_options = [], rating_options = [] }) => {
    const { contact, account, outcome, close_reason, pipeline_stage_summary, pipeline_custom_data,
        pipeline_master_source, pipeline_secondary_source, created_by, last_modified_by } = lead;

    const { fields } = pipeline_custom_data || {};

    const { account_id } = useParams();

    const leadPriority = priority_options.find(option => option.value === fields?.priority)?.text;
    const leadRating = rating_options.find(option => option.value === fields?.rating)?.text;
    return (
        <Table.Row>
            {!isColumnHidden(KEY_CREATED) && (
                <Table.Cell disabled={outcome}>
                    <div><b>{DateTimeUtils.formatEpoch(lead.pipeline_created, DateTimeUtils.WORD_DATE_FORMAT)}</b></div>
                    <div>{DateTimeUtils.formatEpoch(lead.pipeline_created, DateTimeUtils.TIME_FORMAT)}</div>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CREATED_BY) && (
                <Table.Cell>
                    {created_by && (
                        <Person
                            username={created_by?.username}
                            email={created_by?.email}
                            isInactiveUser={!created_by?.active}
                        />
                    )}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_LAST_MODIFIED) && (
                <Table.Cell disabled={outcome}>
                    <div><b>{lead.pipeline_activity && DateTimeUtils.formatEpoch(lead.pipeline_activity, DateTimeUtils.WORD_DATE_FORMAT)}</b></div>
                    <div>{lead.pipeline_activity && DateTimeUtils.formatEpoch(lead.pipeline_activity, DateTimeUtils.TIME_FORMAT)}</div>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_LAST_MODIFIED_BY) && (
                <Table.Cell disabled={outcome}>
                    {last_modified_by && (
                        <Person 
                            username={last_modified_by?.username}
                            email={last_modified_by?.email}
                            isInactiveUser={!last_modified_by?.active}
                        />
                    )}
                </Table.Cell>
            )}
            {account_id && (
                <Table.Cell>
                    <Contact
                        contact={{
                            ...contact,
                            email: contact?.primary_email || {},
                            phone: contact?.primary_phone || {}
                        }}
                        name={`${contact?.first_name} ${contact?.last_name}`}
                        jobTitle={contact?.job_title || ''}
                        linkToContact={
                            <RestrictedView type="link" userPermission={USER_PERMISSIONS.CONTACTS_VIEW} userRestrict assignmentIds={[(lead?.contact?.owner_id || 0), (lead?.contact?.coowner_id || 0)]}>
                                <Link to={`/contact/${contact?.id}`}>{`${contact?.first_name} ${contact?.last_name}`}</Link>
                            </RestrictedView>
                        }
                    />
                </Table.Cell>
            )}
            <Table.Cell>
                {
                    lead.pipeline_id && (
                        <ModuleLink pipeline_id={lead.pipeline_id} record={lead}/>
                    )
                }
            </Table.Cell>
            {!isColumnHidden(KEY_ACCOUNT) && (
                <Table.Cell disabled={outcome}>
                    {account?.id &&
                        <span>
                            {account?.name}
                        </span>
                    }
                </Table.Cell>
            )
            }
            {
                !isColumnHidden(KEY_STAGE) && (
                    <Table.Cell disabled={outcome}>
                        <Header as='h5' disabled={outcome}>
                            {pipeline_stage_summary?.pipeline_category_name}
                            <Header.Subheader>
                                {pipeline_stage_summary?.pipeline_stage_name}
                            </Header.Subheader>
                        </Header>
                    </Table.Cell>
                )
            }
            {
                !isColumnHidden(KEY_OUTCOME) && (
                    <Table.Cell disabled={outcome}>
                        {outcome &&
                            <span className={`ModuleRow__outcome--${outcome === 'lost' ? 'lost' : 'won'}`}>
                                <b>{outcome.charAt(0).toUpperCase() + outcome.slice(1).replace('(', ' (')}</b>
                            </span>
                        }
                    </Table.Cell>
                )
            }
            {
                !isColumnHidden(KEY_LOST_REASON) && (
                    <Table.Cell disabled={outcome}>
                        {close_reason?.id &&
                            <span>
                                {close_reason?.name}
                            </span>
                        }
                    </Table.Cell>
                )
            }
            {
                !isColumnHidden(KEY_LEAD_TYPE) && (
                    <Table.Cell disabled={outcome}>
                        {fields?.type}
                    </Table.Cell>
                )
            }
            {
                !isColumnHidden(KEY_PRODUCT_INTEREST) && (
                    <Table.Cell disabled={outcome}>
                        {lead.product_interests?.length > 0 && (
                            <CollapsibleList
                                minimumNumberOfItemsToShow={3}
                                items={lead.product_interests?.map(product_interest => (product_interest.name))}
                            />
                        )}
                    </Table.Cell>
                )
            }
            {
                !isColumnHidden(KEY_LEAD_PRIORITY) && (
                    <Table.Cell disabled={outcome}>
                        {fields?.priority &&
                            <React.Fragment>
                                <PriorityIcon type={leadPriority?.toLowerCase()} /> {leadPriority}
                            </React.Fragment>
                        }
                    </Table.Cell>
                )
            }
            {
                !isColumnHidden(KEY_LEAD_RATING) && (
                    <Table.Cell disabled={outcome}>
                        {fields?.rating &&
                            <React.Fragment>
                                <TemperatureIcon type={leadRating?.toLowerCase()} /> {leadRating}
                            </React.Fragment>
                        }
                    </Table.Cell>
                )
            }
            {
                !isColumnHidden(KEY_LEAD_SCORE) && (
                    <Table.Cell disabled={outcome}>
                        {fields?.score}
                    </Table.Cell>
                )
            }
            {
                !isColumnHidden(KEY_MASTER_SECONDARY) && (
                    <Table.Cell disabled={outcome}>
                        {`${pipeline_master_source ? pipeline_master_source : ''}${pipeline_secondary_source ? `/${pipeline_secondary_source.name}` : ''}`}
                    </Table.Cell>
                )
            }
            {
                !isColumnHidden(KEY_CAMPAIGN_SOURCE) && (
                    <Table.Cell disabled={outcome}>

                    </Table.Cell>
                )
            }
            {
                !isColumnHidden(KEY_CLOSE_PROBABILITY) && (
                    <Table.Cell disabled={outcome}>
                        {fields?.close_probability}
                    </Table.Cell>
                )
            }
            {
                !isColumnHidden(KEY_TOTAL_COST) && (
                    <Table.Cell disabled={outcome}>

                    </Table.Cell>
                )
            }
            {
                !isColumnHidden(KEY_ACQUISITION_COST) && (
                    <Table.Cell disabled={outcome}>
                        {fields?.acquisition_cost}
                    </Table.Cell>
                )
            }
            {
                !isColumnHidden(KEY_ADDITIONAL_COST) && (
                    <Table.Cell disabled={outcome}>
                        {fields?.additional_cost}
                    </Table.Cell>
                )
            }
            {
                !isColumnHidden(KEY_EXPECTED_REVENUE) && (
                    <Table.Cell disabled={outcome}>
                        {fields?.expected_revenue}
                    </Table.Cell>
                )
            }
        </Table.Row >
    )
}

export default LeadsRow;
